import type { ReactNode } from "react";
import type { Behaviour } from "../lib/types";
import { Field } from "./ui";

export const TONES: [string, string][] = [
  ["friendly", "Friendly"],
  ["professional", "Professional"],
  ["warm", "Warm and caring"],
  ["concise", "Concise"],
  ["enthusiastic", "Enthusiastic"],
];
export const LENGTHS: [string, string][] = [
  ["short", "Short (1-3 sentences)"],
  ["medium", "Medium (2-5 sentences)"],
  ["detailed", "Detailed when needed"],
];
export const PURPOSES: [string, string][] = [
  ["sales", "Sales: turn visitors into leads"],
  ["booking", "Bookings and appointments"],
  ["support", "Customer support"],
  ["general", "General questions"],
];
export const LANGUAGES: [string, string][] = [
  ["auto", "Visitor's language (automatic)"],
  ["ar", "Arabic"], ["en", "English"], ["fr", "French"], ["hi", "Hindi"], ["ur", "Urdu"], ["fa", "Persian"], ["tr", "Turkish"],
  ["ml", "Malayalam"], ["ta", "Tamil"], ["bn", "Bengali"], ["tl", "Filipino"], ["id", "Indonesian"], ["ms", "Malay"], ["es", "Spanish"],
  ["de", "German"], ["it", "Italian"], ["pt", "Portuguese"], ["ru", "Russian"], ["zh", "Chinese"], ["ja", "Japanese"], ["ko", "Korean"], ["nl", "Dutch"],
];

// Styles of Arabic (same keys as the server).
export const DIALECTS: [string, string][] = [
  ["gulf", "Gulf (Khaleeji)"], ["bahraini", "Bahraini"], ["saudi", "Saudi"], ["kuwaiti", "Kuwaiti"], ["emirati", "Emirati"],
  ["qatari", "Qatari"], ["omani", "Omani"], ["egyptian", "Egyptian"], ["levantine", "Levantine"], ["iraqi", "Iraqi"],
  ["msa", "Modern Standard Arabic (فصحى)"],
];

export const emptyBehaviour: Behaviour = {
  name: "",
  purpose: "sales",
  tone: "friendly",
  response_length: "short",
  language: "auto",
  dialect: "gulf",
  mirror_language: true,
  instructions: "",
  greeting: "Hi! How can I help you today?",
  lead_capture: "offer",
  temperature: 0.3,
  starter_prompts: [],
  qualification_questions: [],
  handoff_triggers: [],
  restricted_topics: [],
  create_deals: false,
  can_book: false,
};

// Pick only the behaviour fields from an agent, template or draft.
export function behaviourOf(src: Partial<Behaviour>): Behaviour {
  const out = { ...emptyBehaviour };
  (Object.keys(emptyBehaviour) as (keyof Behaviour)[]).forEach((k) => {
    if (src[k] !== undefined && src[k] !== null) (out as Record<string, unknown>)[k] = src[k];
  });
  return out;
}

const placeholderPattern = /\[[^\]\n]{2,80}\]/g;
export const placeholders = (text: string) => text.match(placeholderPattern) ?? [];

type Limit = (key: string) => number | undefined;

// AgentBuilder edits an agent's behaviour. It is controlled: the parent keeps
// the value, so the test lab can try unsaved changes.
export function AgentBuilder({
  value,
  onChange,
  limit,
  leadCaptureAllowed = true,
  handoffAllowed = true,
  dealsAllowed = true,
  bookingAllowed = false,
  nameLabel = "Agent name",
  top,
  knowledge,
}: {
  value: Behaviour;
  onChange: (v: Behaviour) => void;
  limit: Limit;
  leadCaptureAllowed?: boolean;
  handoffAllowed?: boolean;
  dealsAllowed?: boolean;
  bookingAllowed?: boolean;
  nameLabel?: string;
  top?: ReactNode;
  /** knowledge replaces the general note about Knowledge (e.g. with the company's own sources). */
  knowledge?: ReactNode;
}) {
  const set = <K extends keyof Behaviour>(k: K, v: Behaviour[K]) => onChange({ ...value, [k]: v });
  const missing = placeholders(value.instructions);
  const maxInstructions = limit("agent_instructions_length");
  return (
    <div className="builder">
      <fieldset>
        <legend>Identity</legend>
        {top}
        <div className="row">
          <Field label={nameLabel}>
            <input value={value.name} maxLength={limit("agent_name_length")} onChange={(e) => set("name", e.target.value)} required />
          </Field>
          <Field label="Main job">
            <select value={value.purpose} onChange={(e) => set("purpose", e.target.value)}>
              {PURPOSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
        <div className="row">
          <Field label="Tone">
            <select value={value.tone} onChange={(e) => set("tone", e.target.value)}>
              {TONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Reply length">
            <select value={value.response_length} onChange={(e) => set("response_length", e.target.value)}>
              {LENGTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Language">
            <select value={value.language} onChange={(e) => set("language", e.target.value)}>
              {LANGUAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Arabic style">
            <select value={value.dialect ?? "gulf"} onChange={(e) => set("dialect", e.target.value)}>
              {DIALECTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
        </div>
        <label className="check">
          <input type="checkbox" checked={value.mirror_language ?? true} onChange={(e) => set("mirror_language", e.target.checked)} />
          Follow the customer: answer in the language and Arabic dialect they write in (including Arabic in English letters)
        </label>
      </fieldset>

      <fieldset>
        <legend>First impression</legend>
        <Field label="Greeting">
          <input value={value.greeting} maxLength={limit("agent_greeting_length")} onChange={(e) => set("greeting", e.target.value)} />
        </Field>
        <ListEditor
          label="Suggested questions (buttons visitors can tap)"
          items={value.starter_prompts}
          onChange={(v) => set("starter_prompts", v)}
          max={limit("agent_starter_prompts")}
          itemMax={80}
          placeholder="e.g. How much does it cost?"
        />
      </fieldset>

      <fieldset>
        <legend>Instructions and key facts</legend>
        <Field label="The essentials it always knows: services, prices, hours, policies, how to help">
          <textarea rows={14} value={value.instructions} maxLength={maxInstructions} onChange={(e) => set("instructions", e.target.value)} />
        </Field>
        <p className="muted small">
          {value.instructions.length.toLocaleString()}
          {maxInstructions ? ` / ${maxInstructions.toLocaleString()}` : ""} characters.
          {missing.length > 0 && (
            <span className="warn-text"> {missing.length} placeholder{missing.length === 1 ? "" : "s"} still to fill in: {missing.slice(0, 4).join(", ")}{missing.length > 4 ? "…" : ""}. The agent treats these as unknown.</span>
          )}
        </p>
        {knowledge ?? <p className="muted small">For long documents (menus, price lists, FAQs), use Knowledge instead: the agent looks up the relevant parts for each question.</p>}
      </fieldset>

      <fieldset>
        <legend>Turning visitors into customers</legend>
        <Field label="Follow-up">
          <select value={value.lead_capture} disabled={!leadCaptureAllowed} onChange={(e) => set("lead_capture", e.target.value as Behaviour["lead_capture"])}>
            <option value="offer">Offer follow-up when the visitor is interested, and save their details</option>
            <option value="off">Never ask for contact details</option>
          </select>
        </Field>
        {value.lead_capture === "offer" && leadCaptureAllowed && dealsAllowed && (
          <label className="inline small">
            <input type="checkbox" checked={!!value.create_deals} onChange={(e) => set("create_deals", e.target.checked)} />
            Open a deal in your pipeline for each new lead
          </label>
        )}
        {bookingAllowed && (
          <label className="inline small">
            <input type="checkbox" checked={!!value.can_book} onChange={(e) => set("can_book", e.target.checked)} />
            Book appointments in chat (checks your calendar's free times; set up services under Calendar)
          </label>
        )}
        {value.lead_capture === "offer" && leadCaptureAllowed && (
          <ListEditor
            label="What to learn about an interested visitor (asked naturally, one at a time)"
            items={value.qualification_questions}
            onChange={(v) => set("qualification_questions", v)}
            max={limit("agent_qualification_questions")}
            itemMax={200}
            placeholder="e.g. Budget range"
          />
        )}
      </fieldset>

      <fieldset>
        <legend>Boundaries</legend>
        {handoffAllowed && (
          <ListEditor
            label="Hand over to your team when…"
            items={value.handoff_triggers}
            onChange={(v) => set("handoff_triggers", v)}
            max={limit("agent_handoff_rules")}
            itemMax={200}
            placeholder="e.g. The visitor wants a refund"
          />
        )}
        <ListEditor
          label="Topics to politely decline"
          items={value.restricted_topics}
          onChange={(v) => set("restricted_topics", v)}
          max={limit("agent_restricted_topics")}
          itemMax={200}
          placeholder="e.g. Medical advice"
        />
        <Field label={`Creativity: ${value.temperature.toFixed(1)} (lower is more predictable)`}>
          <input type="range" min={0} max={1} step={0.1} value={value.temperature} onChange={(e) => set("temperature", Number(e.target.value))} />
        </Field>
      </fieldset>
    </div>
  );
}

export function ListEditor({
  label,
  items,
  onChange,
  max,
  itemMax,
  placeholder,
}: {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  max?: number;
  itemMax: number;
  placeholder?: string;
}) {
  const full = max !== undefined && items.length >= max;
  return (
    <div className="field list-editor">
      <span>
        {label} {max !== undefined && <em className="muted">({items.length}/{max})</em>}
      </span>
      {items.map((item, i) => (
        <div className="inline" key={i}>
          <input
            value={item}
            maxLength={itemMax}
            placeholder={placeholder}
            aria-label={`${label} ${i + 1}`}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))}
          />
          <button type="button" className="secondary" aria-label="Remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      {max === 0 ? (
        <span className="muted small">Not included in your plan.</span>
      ) : (
        <button type="button" className="link small" disabled={full} onClick={() => onChange([...items, ""])}>
          {full ? "Plan limit reached" : "+ Add"}
        </button>
      )}
    </div>
  );
}

// Drops empty list items before sending.
export function cleanBehaviour(v: Behaviour): Behaviour {
  const clean = (l: string[]) => l.map((x) => x.trim()).filter(Boolean);
  return {
    ...v,
    name: v.name.trim(),
    starter_prompts: clean(v.starter_prompts),
    qualification_questions: clean(v.qualification_questions),
    handoff_triggers: clean(v.handoff_triggers),
    restricted_topics: clean(v.restricted_topics),
  };
}
